import { useCallback, useMemo } from 'react';
import { needsFullVideoPlan, type StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { useGetCatalogSongQuery } from '../../../../../redux/api/fymAPI';
import type { CatalogItem, CreateKitAssetItem } from '../../../../../redux/api/fymAPI.types';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useIsDemoAccount } from '../../../../accountSelector/useIsDemoAccount';
import { useVideoCredits } from '../../credits/useVideoCredits';
import { itemsCredits, daysToRelease } from '../../studio/studioRules';
import { useMakeKitAssets } from '../assets/useMakeKitAssets';
import { useKitNavigation } from '../assets/useKitNavigation';
import {
  KitSlotView,
  KitSongContext,
  missingKitBlocks,
  missingKitItems,
  missingSlotCount,
  slotItems,
} from './kitSlots';

/**
 * What the Kit tab does (design assets-rethink b1, d2, d3): make one slot with the Studio defaults
 * (or open the Studio when the slot needs a choice), try a failed slot again with its own params,
 * "Make the N missing ones" as one batch, and the plan offer of a locked slot. The make calls go
 * through `useMakeKitAssets`, so the credit gate and the refusal toasts are those of every kit
 * button.
 */
export function useKitActions(input: { accountId: number; item: CatalogItem; slots: KitSlotView[] }) {
  const { accountId, item, slots } = input;
  const { canUseAiReels, canRenderFullSongVideos } = useMusic24Capabilities();
  const isDemo = useIsDemoAccount();
  const { credits } = useVideoCredits({ accountId });
  const openUpgrade = useUpgradePrompt();
  const navigation = useKitNavigation(item);
  const { make, isMaking } = useMakeKitAssets({ accountId, catalogItemUuid: item.uuid });
  const { data: songFiles } = useGetCatalogSongQuery({ accountId, itemUuid: item.uuid });

  const song = useMemo<KitSongContext>(
    () => ({
      masterReady: item.song.masterStatus === 'ready',
      daysToRelease: daysToRelease(item.releaseDate, new Date()),
      canUseAiReels,
      hasLyrics: item.song.hasLyrics === true,
      canRenderFullSongVideos: credits?.canRenderFullSongVideos ?? canRenderFullSongVideos,
      hookStartSec: songFiles?.song.master?.hookStartSec ?? null,
      durationSec: songFiles?.song.master?.durationSec ?? item.song.durationSec ?? null,
    }),
    [canRenderFullSongVideos, canUseAiReels, credits?.canRenderFullSongVideos, item, songFiles]
  );

  const missing = useMemo(() => missingKitItems(slots, song), [slots, song]);
  // The button counts slots, as the chips do (QA 2026-10-28): one slot can make more than one file.
  const missingSlots = useMemo(() => missingSlotCount(slots, song), [slots, song]);
  const missingBlocks = useMemo(() => missingKitBlocks(slots, song), [slots, song]);

  const makeSlot = useCallback(
    (view: KitSlotView) => {
      const { themeId } = view.slot;
      if (themeId === null) {
        return;
      }
      const items = slotItems(view, song);
      if (items === null) {
        // The slot needs a choice (or the song lacks a part): the Studio says which.
        navigation.openTheme(themeId);
        return;
      }
      make(items, items.length > 1).catch(console.error);
    },
    [make, navigation, song]
  );

  /** "Try again": the params of the failed revisions, so the next revision is the same asset. */
  const tryAgain = useCallback(
    (view: KitSlotView) => {
      const { themeId } = view.slot;
      const failed = view.cards
        .filter((card) => card.state === 'failed' && card.asset !== null)
        .map((card): CreateKitAssetItem => ({ themeId: themeId as StudioThemeId, params: card.asset?.params ?? {} }));
      if (failed.length === 0) {
        makeSlot(view);
        return;
      }
      make(failed, failed.length > 1).catch(console.error);
    },
    [make, makeSlot]
  );

  const makeMissing = useCallback(() => {
    make(missing, true).catch(console.error);
  }, [make, missing]);

  /** "Make the kit" of an empty Kit (design d2): the images and the Canvas; the long videos wait. */
  const makeKit = useCallback(() => {
    const items = missing.filter((one) => !needsFullVideoPlan(one.themeId));
    make(items, true).catch(console.error);
  }, [make, missing]);

  const offerPlan = useCallback(() => openUpgrade(SMLUpgradeFeature.generic, item.title), [item.title, openUpgrade]);

  return {
    song,
    isMaking,
    canAct: !isDemo,
    isOwner: credits?.isOwner ?? true,
    creditsLeft: credits?.totalLeft ?? null,
    missingCost: itemsCredits(missing),
    missingCount: missingSlots,
    missingBlocks,
    makeSlot,
    tryAgain,
    makeMissing,
    makeKit,
    offerPlan,
    openStudio: navigation.openStudio,
    openTheme: navigation.openTheme,
  };
}

export type KitActions = ReturnType<typeof useKitActions>;
